import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagicWand } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const LABELS = { slabs: "Slab photos", remnants: "Remnant photos", sinks: "Sink photos" };

// One click: count what still needs the HD pass, then queue it and watch the queue drain.
export const EnhanceAllDialog = ({ open, onClose }) => {
  const [cols, setCols] = useState(["slabs"]);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPlan(null);
    api
      .post("/photos/enhance-all", { collections: cols, dry_run: true })
      .then(({ data }) => setPlan(data))
      .catch((err) =>
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) || "Could not count photos",
        ),
      );
  }, [open, cols]);

  useEffect(() => {
    if (!open || !started) return undefined;
    const tick = () =>
      api
        .get("/photos/enhance-status")
        .then(({ data }) => setStatus(data))
        .catch(() => {});
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [open, started]);

  const toggle = (c) => (v) =>
    setCols((list) => (v ? [...new Set([...list, c])] : list.filter((x) => x !== c)));

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/photos/enhance-all", {
        collections: cols,
        dry_run: false,
      });
      setPlan(data);
      setStarted(true);
      toast.success(
        data.queued
          ? `${data.queued} photo${data.queued === 1 ? "" : "s"} queued for HD`
          : "Nothing to enhance right now",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not start the HD pass",
      );
    }
    setBusy(false);
  };

  const total = plan?.total ?? 0;
  const allowance = plan?.allowance ?? 0;
  const willQueue = Math.min(total, allowance);
  const remaining = status?.pending ?? 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="enhance-all-dialog"
        className="max-w-lg border-zinc-800 bg-zinc-900"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MagicWand size={18} weight="bold" className="text-emerald-400" />
            Enhance all photos
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Older stock pictures get the same HD treatment as new uploads — upscaled
            to {plan?.size || "2K"}, sharpened and color-corrected, originals kept.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950 p-3">
          {Object.keys(LABELS).map((c) => (
            <label
              key={c}
              className="flex items-center justify-between gap-3 text-sm text-zinc-200"
            >
              <span className="flex items-center gap-3">
                <Checkbox
                  data-testid={`enhance-all-col-${c}`}
                  checked={cols.includes(c)}
                  disabled={started}
                  onCheckedChange={toggle(c)}
                />
                {LABELS[c]}
              </span>
              <span
                className="font-mono-data text-xs text-zinc-400"
                data-testid={`enhance-all-count-${c}`}
              >
                {plan?.counts?.[c] ?? (cols.includes(c) ? "…" : "—")}
                {plan?.counts?.[c] !== undefined ? " to do" : ""}
              </span>
            </label>
          ))}
        </div>

        {plan && !started && (
          <p className="text-sm text-zinc-300" data-testid="enhance-all-plan">
            {total === 0
              ? "Every photo in the selected groups is already HD."
              : willQueue < total
                ? `${total} photos need HD. Today's allowance covers ${willQueue}; run this again tomorrow for the rest, or raise the caps in Settings → AI Images.`
                : `${total} photo${total === 1 ? "" : "s"} will be enhanced now (about ${Math.ceil(total * 0.25)} min in the background).`}
          </p>
        )}

        {started && (
          <div
            className="rounded-md border border-emerald-900/50 bg-emerald-950/30 p-3 text-sm"
            data-testid="enhance-all-progress"
          >
            {remaining > 0 ? (
              <span className="text-emerald-200">
                <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                {remaining} still enhancing… you can close this and keep working.
              </span>
            ) : (
              <span className="text-emerald-200">
                Done — {status?.done ?? 0} HD photos in stock
                {status?.failed ? `, ${status.failed} failed` : ""}
                {status?.skipped ? `, ${status.skipped} skipped (cap)` : ""}.
              </span>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          {!started ? (
            <Button
              type="button"
              data-testid="enhance-all-start"
              disabled={busy || !plan || willQueue === 0}
              onClick={start}
              className="h-12 flex-1 bg-emerald-500 text-xs font-bold uppercase tracking-widest text-black hover:bg-emerald-400"
            >
              {busy ? "Queuing…" : `Enhance ${willQueue || ""} photo${willQueue === 1 ? "" : "s"}`}
            </Button>
          ) : null}
          <Button
            type="button"
            data-testid="enhance-all-close"
            variant="outline"
            onClick={onClose}
            className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest"
          >
            {started ? "Close" : "Cancel"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
